export type LegacyTaxTreatment = "iva_21" | "reverse_charge";
export type LegacyTaxEligibilityFilter = "all" | "eligible" | "excluded";

export type LegacyTaxBulkFilters = {
  search: string;
  status: string;
  eligibility: LegacyTaxEligibilityFilter;
};

export type LegacyTaxBulkRow = {
  id: number;
  status: string;
  canApply: boolean;
  snapshotToken: string;
  invoiceNumber?: string | null;
  customerName?: string | null;
  contractCode?: string | null;
  contractTitle?: string | null;
  installationName?: string | null;
  label?: string | null;
  fiscalWithholdingAmount?: unknown;
  invoiceDate?: unknown;
  baseAmount?: unknown;
  taxTreatment?: string | null;
  taxAmount?: unknown;
  historicalAmount?: unknown;
  contractualRetentionAmount?: unknown;
  collectionAmountDisclosure?: string | null;
  collectionOrigin?: "none" | "ledger" | "snapshot" | "unknown";
  invoiceDocumentUrl?: string | null;
  invoiceDocumentAvailable?: boolean;
  requiresDocumentAttestation?: boolean;
  exclusionReasons?: string[];
};

export type LegacyTaxSnapshotSelection = { id: number; snapshotToken: string };
export type LegacyTaxRowDraft = {
  treatment?: LegacyTaxTreatment;
  withholdingAmount?: string;
  reason?: string;
  documentBase?: string;
  documentTotal?: string;
  documentAmountsAttested?: boolean;
};
export type LegacyTaxBulkDefaults = {
  treatment?: LegacyTaxTreatment;
  withholdingAmount: string;
  withholdingAttested: boolean;
  documentAmountsAttested: boolean;
  reason: string;
};

export type LegacyTaxBulkViewState = {
  filters: LegacyTaxBulkFilters;
  selection: LegacyTaxSnapshotSelection[];
};

export type LegacyTaxBulkViewAction =
  | { type: "filter_changed"; filters: LegacyTaxBulkFilters }
  | { type: "toggle"; row: LegacyTaxBulkRow; checked: boolean }
  | { type: "select_all_filtered"; rows: LegacyTaxBulkRow[] }
  | { type: "clear_selection" }
  | { type: "reconcile_rows"; rows: LegacyTaxBulkRow[] };

export const EMPTY_LEGACY_TAX_FILTERS: LegacyTaxBulkFilters = {
  search: "",
  status: "all",
  eligibility: "all",
};

export function createLegacyTaxBulkViewState(): LegacyTaxBulkViewState {
  return { filters: { ...EMPTY_LEGACY_TAX_FILTERS }, selection: [] };
}

function normalizeSearch(value: string): string {
  return value.trim().toLocaleLowerCase("es-ES").normalize("NFD").replace(/[\u0300-\u036f]/g, "");
}

export function filterLegacyTaxRows<T extends LegacyTaxBulkRow>(
  rows: readonly T[],
  filters: LegacyTaxBulkFilters,
): T[] {
  const query = normalizeSearch(filters.search);
  return rows.filter((row) => {
    if (filters.status !== "all" && row.status !== filters.status) return false;
    if (filters.eligibility === "eligible" && !row.canApply) return false;
    if (filters.eligibility === "excluded" && row.canApply) return false;
    if (!query) return true;
    const searchable = [
      row.invoiceNumber,
      row.customerName,
      row.contractCode,
      row.contractTitle,
      row.installationName,
      row.label,
      String(row.id),
    ].filter((value) => value !== null && value !== undefined)
      .map((value) => normalizeSearch(String(value)));
    return searchable.some((value) => value.includes(query));
  });
}

export function selectEligibleSnapshot(rows: readonly LegacyTaxBulkRow[]): LegacyTaxSnapshotSelection[] {
  const seen = new Set<number>();
  const selected: LegacyTaxSnapshotSelection[] = [];
  for (const row of rows) {
    if (!row.canApply || !row.snapshotToken || seen.has(row.id)) continue;
    seen.add(row.id);
    selected.push({ id: row.id, snapshotToken: row.snapshotToken });
  }
  return selected;
}

export function toggleSnapshotSelection(
  selection: readonly LegacyTaxSnapshotSelection[],
  row: LegacyTaxBulkRow,
  checked: boolean,
): LegacyTaxSnapshotSelection[] {
  if (!checked) return selection.filter((item) => item.id !== row.id);
  if (!row.canApply || !row.snapshotToken || selection.some((item) => item.id === row.id)) return [...selection];
  return [...selection, { id: row.id, snapshotToken: row.snapshotToken }];
}

export function pruneSnapshotSelection(
  selection: readonly LegacyTaxSnapshotSelection[],
  rows: readonly LegacyTaxBulkRow[],
): LegacyTaxSnapshotSelection[] {
  const knownIds = new Set(rows.map((row) => row.id));
  const seen = new Set<number>();
  return selection.filter((item) => {
    if (!knownIds.has(item.id) || seen.has(item.id)) return false;
    seen.add(item.id);
    return true;
  });
}

export function isSnapshotSelectionCurrent(
  selection: readonly LegacyTaxSnapshotSelection[],
  rows: readonly LegacyTaxBulkRow[],
): boolean {
  if (!selection.length) return false;
  const byId = new Map(rows.map((row) => [row.id, row]));
  return selection.every((item) => {
    const row = byId.get(item.id);
    return Boolean(row?.canApply && row.snapshotToken === item.snapshotToken);
  });
}

export function legacyTaxBulkViewReducer(
  state: LegacyTaxBulkViewState,
  action: LegacyTaxBulkViewAction,
): LegacyTaxBulkViewState {
  switch (action.type) {
    case "filter_changed":
      return { filters: action.filters, selection: [] };
    case "toggle":
      return { ...state, selection: toggleSnapshotSelection(state.selection, action.row, action.checked) };
    case "select_all_filtered":
      return { ...state, selection: selectEligibleSnapshot(action.rows) };
    case "clear_selection":
      return { ...state, selection: [] };
    case "reconcile_rows":
      return { ...state, selection: pruneSnapshotSelection(state.selection, action.rows) };
  }
}

function hasKnownFiscalWithholding(value: LegacyTaxBulkRow["fiscalWithholdingAmount"]): boolean {
  return value !== null && value !== undefined && String(value).trim() !== "";
}

export function invalidateCommonRetentionAttestation(defaults: LegacyTaxBulkDefaults): LegacyTaxBulkDefaults {
  return defaults.withholdingAttested || defaults.documentAmountsAttested
    ? { ...defaults, withholdingAttested: false, documentAmountsAttested: false }
    : defaults;
}

export type ResolvedLegacyTaxChoice = {
  treatment: LegacyTaxTreatment | undefined;
  withholdingAmount: string;
  withholdingSource: "historical" | "individual" | "common" | "missing";
  reason: string;
  needsCommonRetentionAttestation: boolean;
  documentBase: string;
  documentTotal: string;
  documentAmountsAttested: boolean;
};

export function resolveLegacyTaxChoice(
  row: LegacyTaxBulkRow,
  draft: LegacyTaxRowDraft,
  defaults: LegacyTaxBulkDefaults,
): ResolvedLegacyTaxChoice {
  const knownWithholding = hasKnownFiscalWithholding(row.fiscalWithholdingAmount);
  const individual = draft.withholdingAmount?.trim() ?? "";
  const common = defaults.withholdingAmount.trim();
  let withholdingAmount = "";
  let withholdingSource: ResolvedLegacyTaxChoice["withholdingSource"] = "missing";
  let needsCommonRetentionAttestation = false;

  if (knownWithholding) {
    // The live writer rejects changing a persisted fiscal retention; keep it authoritative.
    withholdingAmount = String(row.fiscalWithholdingAmount).trim();
    withholdingSource = "historical";
  } else if (individual !== "") {
    withholdingAmount = individual;
    withholdingSource = "individual";
  } else if (common !== "" && defaults.withholdingAttested) {
    withholdingAmount = common;
    withholdingSource = "common";
  } else if (common !== "") {
    needsCommonRetentionAttestation = true;
  }

  return {
    treatment: draft.treatment ?? defaults.treatment,
    withholdingAmount,
    withholdingSource,
    reason: draft.reason?.trim() || defaults.reason.trim(),
    needsCommonRetentionAttestation,
    documentBase: draft.documentBase ?? "",
    documentTotal: draft.documentTotal ?? "",
    documentAmountsAttested: draft.documentAmountsAttested ?? defaults.documentAmountsAttested,
  };
}

export function createLegacyTaxPreviewFingerprint(
  selection: readonly LegacyTaxSnapshotSelection[],
  currentRows: readonly LegacyTaxBulkRow[],
  inputs: readonly unknown[],
): string {
  return JSON.stringify({
    selection: selection.map(({ id, snapshotToken }) => [id, snapshotToken]),
    currentRows: currentRows.map(({ id, snapshotToken, canApply }) => [id, snapshotToken, canApply]),
    inputs,
  });
}

export function splitLegacyTaxChunks<T>(items: readonly T[], maxItems = 100): T[][] {
  if (!Number.isInteger(maxItems) || maxItems < 1) throw new Error("El tamaño del lote debe ser un entero positivo.");
  const chunks: T[][] = [];
  for (let start = 0; start < items.length; start += maxItems) {
    chunks.push(items.slice(start, start + maxItems));
  }
  return chunks;
}
